import React, { useEffect, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import { Breadcrumb, FormGroup, Button, Row, Checkbox, DropdownButton} from "react-bootstrap";
import { LinkContainer } from "react-router-bootstrap";
import { useLocaleContext } from "../../../context/LocaleContext";
import { getSortedMembers } from "../../../../platform/src/helpers/members";
import locale from "../../../locale";
import * as Api from "../../../../api/cp/members";
import { getEventTags } from "../../../../api/cp";
import { eventModuleEpisodeUpdate } from "../../../../api/cp/eventModuleEpisode";
import { membersVisibility } from "./models";
import ParticipantsTable from "./ParticipantsTable";
import ParticipantModal from "./ParticipantsModals/ParticipantModal";
import RoleCreateModal from "./ParticipantsModals/RoleCreateModal";
import { Event } from "../../Comet";

const Participants = ({ event }) => {
    const $locale = useLocaleContext()
    const [participants, setParticipants] = useState([]);
    const [roles, setRoles] = useState([]);
    const [tags, setTags] = useState([]);
    const [ids, setIds] = useState([]);
    const [editableParticipant, setEditableParticipant] = useState(null);
    const [sortMembers, setSortMembers] = useState(!!event.moduleEpisode.sortMembers || false);
    const [isParticipantModalVisible, setParticipantModalVisible] = useState(false);
    const [isRoleModalVisible, setRoleModalVisible] = useState(false);

    useEffect(() => {
        getParticipantsList(sortMembers);
        getRolesList();
        getTagsList();
    }, []);

    async function getParticipantsList(sortMembers) {
        try {
            let response = await Api.getEventMembers(event.eventKey);

            if (response && sortMembers) {
                response = getSortedMembers(response, $locale, 'lastname', 'en');
            }

            setParticipants(response || []);
        } catch (error) {
            console.error(error);
        }
    }

    async function getRolesList() {
        try {
            const response = await Api.getEventRoles(event.eventKey);
            setRoles(response || []);
        } catch (error) {
            console.error(error);
        }
    }

    async function getTagsList() {
        try {
            const response = await getEventTags(event.eventKey);
            setTags(response || []);
        } catch (error) {
            console.error(error);
        }
    }

    const handleParticipantSave = async (model) => {
        try {
            const formData = new FormData();
            Object.keys(model).forEach(key => formData.append(key, model[key]));

            const response = await Api.createEventMember(event.eventKey, formData);
            return response;
        } catch (error) {
            console.error(error);
        }
    };

    const handleOpenEditModal = (model) => {
        setParticipantModalVisible(true);
        setEditableParticipant(model);
    };

    const handleParticipantEdit = async (model) => {
        try {
            const formData = new FormData();
            Object.keys(model).forEach(key => formData.append(key, model[key]));

            const response = await Api.updateEventMember(model.id, formData);
            getParticipantsList(sortMembers);
            setEditableParticipant(null);
            return response;
        } catch (error) {
            console.error(error);
        }
    };

    const handleParticipantDelete = async (id) => {
        try {
            const allow = confirm(locale('participants.removeMember'));

            if (!allow) return;

            await Api.deleteEventMember(id);
            getParticipantsList(sortMembers);
        } catch (error) {
            console.error(error);
        }
    };

    const handleRoleSave = async (model) => {
        try {
            const response = await Api.createEventRoles(event.eventKey, model);
            setRoles((prevState) => ([...prevState, response]));
        } catch (error) {
            console.error(error);
        }
    };

    const handleRoleEdit = async (model) => {
        try {
            await Api.updateEventRoles(model.id, model);
            getRolesList();
        } catch (error) {
            console.error(error);
        }
    };

    const handleRoleDelete = async (id) => {
        try {
            await Api.deleteEventRoles(id);
            getRolesList();
        } catch (error) {
            console.error(error);
        }
    };

    const handleChangeParticipantsOrder = async (list) => {
        try {
            const payload = list.map(({id}) => id);
            setParticipants(list);
            await Api.updateEventMembersOrder(event.eventKey, payload);
            getParticipantsList(sortMembers);
        } catch (error) {
            console.error(error);
        }
    };

    const handleImportExcel = () => {
        const fileInput = document.createElement('input');
        fileInput.setAttribute('type', 'file');
        fileInput.setAttribute('accept', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
        fileInput.addEventListener(
            'change',
            async (e) => {
                const file = e.target.files[0];

                if (!file) return;

                try {
                    const formData = new FormData();
                    formData.append('file', file);

                    const response = await Api.importMembersList(event.eventKey, formData);
                    setParticipants((prevState) => ([...prevState, ...response]));
                } catch (error) {
                    console.error(error);
                    toast.error('Произошла ошибка загрузки файла, попробуйте снова', {
                        duration: 4000,
                    });
                }
            },
            false,
        );
        fileInput.click();
    }

    const clearParticipantsList = async () => {
        const allow = confirm(locale('participants.clear_question'));

        if (!allow) return;

        await Api.clearMembersList(event.eventKey);
        setParticipants([]);
    };

    const handleSortMembers = async (checked) => {
        try {
            await eventModuleEpisodeUpdate(event.eventKey, {sortMembers: checked ? 'lastname' : ''});
            setSortMembers(checked);
            getParticipantsList(checked);
        } catch (error) {
            console.error(error);
        }
    };

    const handleHideMember = async ({ ids, hide }) => {
        try {
            await Api.updateMembersGroupAction(event.eventKey, { ids, hide });
            const _participants = participants.map((participant) => {
                if (!ids.includes(participant.id)) {
                    return participant;
                }

                return {
                    ...participant,
                    hide,
                }
            });
            setParticipants(_participants);
            setIds([]);
        } catch (error) {
            console.error(error);
        }
    };

    const handleToggleMember = (memberId) => {
        if (ids.includes(memberId)) {
            setIds(ids.filter((id) => id !== memberId));
            return;
        }

        setIds((ids) => ([ ...ids, memberId ]));
    };

    const handleToggleAllMembers = () => {
        if (ids.length === participants?.length) {
            setIds([]);
            return;
        }

        setIds(participants.map(({ id }) => id));
    };

    const handleCometMemberCreated = (member) => {
        setParticipants((prev) => [...prev, member]);
    };
    const handleCometMemberUpdated = (member) => {
        if (!participants.length) {
            return;
        }

        const _participants = participants.map((participant) => {
            return participant.id === member.id ? member : participant;
        });
        setParticipants(_participants);
    };
    const handleCometMemberDeleted = (member) => {
        setParticipants((prev) => prev.filter(({id}) => id !== member.id))
    };

    return (
        <section>
            <Event event="create:member" handler={handleCometMemberCreated} />
            <Event event="update:member" handler={handleCometMemberUpdated} />
            <Event event="delete:member" handler={handleCometMemberDeleted} />

            <Breadcrumb>
                <LinkContainer to={`/event/${event.eventPassword}`}>
                    <Breadcrumb.Item>{locale('mainMenu.schedule')}</Breadcrumb.Item>
                </LinkContainer>
                <Breadcrumb.Item active>{locale('mainMenu.schedule.participants')}</Breadcrumb.Item>
            </Breadcrumb>
            <FormGroup>
                <Button onClick={() => setParticipantModalVisible(true)}>
                    Добавить участника
                </Button>
                {' '}
                <Button onClick={() => setRoleModalVisible(true)}>
                    Дополнительный признак
                </Button>
                <div style={{ float: 'right',  textAlign: 'right' }}>
                    <Button onClick={handleImportExcel}>Импорт из Excel</Button>
                    {' '}
                    <Button>
                        <a
                            href={`/api/cp/excel/members/sample.xlsx`}
                            style={{ textDecoration: 'none', color: 'inherit' }}
                        >
                            Скачать шаблон Еxcel
                        </a>
                    </Button>
                    {
                        participants.length !== 0  &&
                        <>
                            {' '}
                            <Button
                                bsStyle="danger"
                                onClick={() => clearParticipantsList()}
                            >
                                Очистить список
                            </Button>
                        </>
                        }
                </div>
            </FormGroup>
            <FormGroup>
                <Checkbox
                    checked={sortMembers}
                    onChange={(e) => handleSortMembers(e.target.checked)}
                >
                    Отсортировать список по Фамилии в алфавитном порядке
                </Checkbox>
            </FormGroup>
            {
                participants && (
                <ParticipantsTable
                    participants={participants}
                    roles={roles}
                    tags={tags}
                    ids={ids}
                    onEdit={handleOpenEditModal}
                    onDelete={handleParticipantDelete}
                    onChangeOrder={handleChangeParticipantsOrder}
                    onHideMember={handleHideMember}
                    onToggleMember={handleToggleMember}
                    onToggleAllMembers={handleToggleAllMembers}
                />
            )}
            <div className="group-actions-wrapper" style={{ bottom: !!ids.length ? '0' : '-100%' }}>
                <DropdownButton
                    title="Действия"
                    id="bt-group-actions"
                >
                    <li role="presentation" className="">
                        <a role="menuitem" tabIndex="-1" href="#">
                            Изменить отображение <span className="caret" />
                        </a>
                        <ul className="dropdown-menu sub-menu" aria-labelledby="bt-group-actions">
                            {membersVisibility.map(({ value, label }) =>
                                <li key={value}>
                                    <a
                                        role="menuitem"
                                        tabIndex="-1"
                                        onClick={() => handleHideMember({ ids, hide: value })}
                                    >
                                        {label}
                                    </a>
                                </li>
                            )}
                        </ul>
                    </li>
                </DropdownButton>
            </div>

            <ParticipantModal
                eventKey={event.eventKey}
                editableParticipant={editableParticipant}
                isModalVisible={isParticipantModalVisible}
                onSave={handleParticipantSave}
                onEdit={handleParticipantEdit}
                onClose={() => {
                    setParticipantModalVisible(false);
                    setEditableParticipant(null);
                }}
            />
            <RoleCreateModal
                eventRoles={roles}
                isModalVisible={isRoleModalVisible}
                onSave={handleRoleSave}
                onEdit={handleRoleEdit}
                onDelete={handleRoleDelete}
                onClose={() => setRoleModalVisible(false)}
            />

            <Toaster position="top-right" />
        </section>
    );
};

export default Participants;
